/**
 * Hook for managing opportunity photos with geolocation.
 * 
 * Supports picking images from library or camera on web/native.
 * Tags each photo with device GPS coordinates if available.
 */

import { useState, useCallback, useEffect } from 'react';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import * as Location from 'expo-location';
import { authApi, OpportunityPhoto } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';

interface UseOpportunityPhotosParams {
  portfolioId: number;
  opportunityId: number;
}

export function useOpportunityPhotos({ portfolioId, opportunityId }: UseOpportunityPhotosParams) {
  const [photos, setPhotos] = useState<OpportunityPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationPermission, setLocationPermission] = useState<boolean>(false);

  useEffect(() => {
    requestLocationPermission();
  }, []);

  useEffect(() => {
    if (portfolioId && opportunityId) {
      fetchPhotos();
    }
  }, [portfolioId, opportunityId]);

  const requestLocationPermission = useCallback(async () => {
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      setLocationPermission(status === 'granted');
    } catch {
      setLocationPermission(false);
    }
  }, []);

  const getCurrentLocation = useCallback(async (): Promise<{ latitude: number; longitude: number } | null> => {
    if (!locationPermission) return null;
    try {
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.High,
      });
      return {
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
      };
    } catch (err) {
      logger.warn('Could not get location for photo', 'photos');
      return null;
    }
  }, [locationPermission]);

  const generateThumbnail = useCallback(async (uri: string): Promise<string | null> => {
    try {
      const result = await ImageManipulator.manipulateAsync(
        uri,
        [{ resize: { width: 300 } }],
        { compress: 0.4, format: ImageManipulator.SaveFormat.JPEG, base64: true }
      );
      return result.base64 || null;
    } catch (err) {
      logger.warn('Failed to generate thumbnail', 'photos');
      return null;
    }
  }, []);

  const fetchPhotos = useCallback(async () => {
    if (!portfolioId || !opportunityId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.getOpportunityPhotos(portfolioId, opportunityId);
      if (response.success) {
        setPhotos(response.photos);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load photos';
      logger.error('Failed to fetch photos', err, 'photos');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, opportunityId]);

  const pickFromLibrary = useCallback(async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        setError('Photo library permission is required');
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 0.7,
        base64: true,
        allowsMultipleSelection: true,
        selectionLimit: 10,
      });

      if (result.canceled || !result.assets?.length) return;

      setUploading(true);
      const location = await getCurrentLocation();

      for (const asset of result.assets) {
        if (!asset.base64) continue;
        const thumbnail = asset.uri ? await generateThumbnail(asset.uri) : null;
        await authApi.uploadOpportunityPhoto(portfolioId, opportunityId, {
          filename: asset.fileName || `photo_${Date.now()}.jpg`,
          mime_type: asset.mimeType || 'image/jpeg',
          image_data: asset.base64,
          thumbnail_data: thumbnail,
          latitude: location?.latitude ?? null,
          longitude: location?.longitude ?? null,
          taken_at: new Date().toISOString(),
        });
      }

      await fetchPhotos();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to upload photos';
      logger.error('Failed to pick from library', err, 'photos');
      setError(msg);
    } finally {
      setUploading(false);
    }
  }, [portfolioId, opportunityId, getCurrentLocation, generateThumbnail, fetchPhotos]);

  const takePhoto = useCallback(async () => {
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== 'granted') {
        setError('Camera permission is required');
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        quality: 0.7,
        base64: true,
      });

      if (result.canceled || !result.assets?.length) return;

      setUploading(true);
      const location = await getCurrentLocation();
      const asset = result.assets[0];

      if (!asset.base64) {
        setError('Failed to capture image data');
        return;
      }

      const thumbnail = asset.uri ? await generateThumbnail(asset.uri) : null;
      await authApi.uploadOpportunityPhoto(portfolioId, opportunityId, {
        filename: asset.fileName || `photo_${Date.now()}.jpg`,
        mime_type: asset.mimeType || 'image/jpeg',
        image_data: asset.base64,
        thumbnail_data: thumbnail,
        latitude: location?.latitude ?? null,
        longitude: location?.longitude ?? null,
        taken_at: new Date().toISOString(),
      });

      await fetchPhotos();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to take photo';
      logger.error('Failed to take photo', err, 'photos');
      setError(msg);
    } finally {
      setUploading(false);
    }
  }, [portfolioId, opportunityId, getCurrentLocation, generateThumbnail, fetchPhotos]);

  const deletePhoto = useCallback(async (photoId: number) => {
    try {
      await authApi.deleteOpportunityPhoto(portfolioId, opportunityId, photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (err) {
      logger.error('Failed to delete photo', err, 'photos');
      setError('Failed to delete photo');
    }
  }, [portfolioId, opportunityId]);

  const getPhotoUri = useCallback((photo: OpportunityPhoto): string | null => {
    // For list view we don't have image_data, but we could build a URL
    // For now photos fetched individually include image_data
    return null;
  }, []);

  return {
    photos,
    loading,
    uploading,
    error,
    fetchPhotos,
    pickFromLibrary,
    takePhoto,
    deletePhoto,
    locationPermission,
  };
}
